import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAdminStore } from '../../stores/adminStore';
import { useUIStore } from '../../stores/uiStore';
import { adminApi, type AdminUser } from '../../api/admin';
import { Spinner } from '../../components/ui/Spinner';
import clsx from 'clsx';

export function AdminUserDetailPage() {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);
  const { updateUserInList } = useAdminStore();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Edit mode states - User
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');

  // Edit mode states - License
  const [isEditingLicense, setIsEditingLicense] = useState(false);
  const [editPlanTier, setEditPlanTier] = useState<'starter' | 'pro'>('starter');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editResellerName, setEditResellerName] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (id) {
      loadUser();
    }
  }, [id]);

  async function loadUser() {
    setIsLoading(true);
    try {
      const { user: userData } = await adminApi.getUser(id!);
      setUser(userData);
      initializeEditState(userData);
    } catch (error) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: t('users.loadError'),
      });
      navigate('/admin/users');
    } finally {
      setIsLoading(false);
    }
  }

  function initializeEditState(userData: AdminUser) {
    setEditName(userData.name);
    setEditRole(userData.role as 'user' | 'admin');

    const sub = userData.subscription;
    setEditPlanTier((sub?.planTier as 'starter' | 'pro') || 'starter');
    setEditExpiresAt(sub?.manualExpiresAt ? sub.manualExpiresAt.split('T')[0] : '');
    setEditResellerName(sub?.resellerName || '');
    setEditNotes('');
  }

  async function handleSaveUser() {
    if (!user) return;

    setIsProcessing(true);
    try {
      await adminApi.updateUser(user.id, { name: editName.trim(), role: editRole });
      await loadUser();
      updateUserInList(user.id, { name: editName.trim(), role: editRole });
      setIsEditingUser(false);
      addToast({
        type: 'success',
        title: tCommon('success'),
        message: t('users.updateSuccess'),
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: error.message || t('users.updateError'),
      });
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleSaveLicense() {
    if (!user) return;

    setIsProcessing(true);
    try {
      // Both Starter and Pro are paid plans that require expiration date
      const expiresAt = editExpiresAt
        ? new Date(editExpiresAt + 'T23:59:59').toISOString()
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(); // Default 1 year

      await adminApi.activateLicense({
        userId: user.id,
        planTier: editPlanTier,
        expiresAt,
        resellerName: editResellerName || undefined,
        resellerNotes: editNotes || undefined,
      });

      await loadUser();
      setIsEditingLicense(false);
      addToast({
        type: 'success',
        title: tCommon('success'),
        message: t('license.updateSuccess'),
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: error.message || t('license.updateError'),
      });
    } finally {
      setIsProcessing(false);
    }
  }

  function handleCancelUserEdit() {
    if (user) {
      setEditName(user.name);
      setEditRole(user.role as 'user' | 'admin');
    }
    setIsEditingUser(false);
  }

  function handleCancelLicenseEdit() {
    if (user) {
      initializeEditState(user);
    }
    setIsEditingLicense(false);
  }

  async function handleCancelLicense() {
    if (!user || !confirm(t('license.cancelConfirm'))) return;

    setIsProcessing(true);
    try {
      await adminApi.cancelLicense(user.id);
      await loadUser();
      addToast({
        type: 'success',
        title: tCommon('success'),
        message: t('license.cancelSuccess'),
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: t('license.cancelError'),
      });
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleDeleteUser() {
    if (!user || !confirm(t('users.deleteConfirm'))) return;

    setIsProcessing(true);
    try {
      await adminApi.deleteUser(user.id);
      addToast({
        type: 'success',
        title: tCommon('success'),
        message: t('users.deleteSuccess'),
      });
      navigate('/admin/users');
    } catch (error: any) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: error.message || t('users.deleteError'),
      });
    } finally {
      setIsProcessing(false);
    }
  }

  function formatDate(dateString: string | null | undefined) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function isExpired(expiresAt: string | null | undefined) {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  }

  function getDaysUntilExpiry(expiresAt: string | null | undefined) {
    if (!expiresAt) return null;
    const days = Math.ceil(
      (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    return days;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const sub = user.subscription;
  const isManual = sub?.source === 'manual';
  const isAdmin = user.role === 'admin';
  const expiresAt = isManual ? sub?.manualExpiresAt : sub?.currentPeriodEnd;
  const daysUntilExpiry = getDaysUntilExpiry(expiresAt);
  const expired = isExpired(expiresAt);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/admin/users')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
        </motion.button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <span className="text-sm font-bold text-primary-700 dark:text-primary-400">
              {user.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {user.name}
              {isAdmin && (
                <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded">
                  Admin
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500 dark:text-slate-400">{user.email}</p>
          </div>
        </div>
      </div>

      {/* User Info Section */}
      <div className="space-y-1">
        <div className="flex items-center justify-between px-2 py-1">
          <h2 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
            {t('users.editUser')}
          </h2>
          {!isEditingUser ? (
            <button
              onClick={() => setIsEditingUser(true)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={handleSaveUser}
                disabled={isProcessing || !editName.trim()}
                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isProcessing ? <Spinner size="sm" /> : <CheckIcon className="h-4 w-4" />}
              </button>
              <button
                onClick={handleCancelUserEdit}
                disabled={isProcessing}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
          <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.fields.name')}</span>
          {isEditingUser ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="px-2 py-1 text-sm text-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
            />
          ) : (
            <span className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</span>
          )}
        </div>

        {/* Email */}
        <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
          <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.fields.email')}</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{user.email}</span>
        </div>

        {/* Role */}
        <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
          <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.fields.role')}</span>
          {isEditingUser ? (
            <select
              value={editRole}
              onChange={(e) => setEditRole(e.target.value as 'user' | 'admin')}
              className="px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
            >
              <option value="user">{t('users.roles.user')}</option>
              <option value="admin">{t('users.roles.admin')}</option>
            </select>
          ) : (
            <span className={clsx(
              'px-2 py-0.5 text-xs font-medium rounded',
              isAdmin
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'
            )}>
              {isAdmin ? t('users.roles.admin') : t('users.roles.user')}
            </span>
          )}
        </div>

        {/* Member Since */}
        <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
          <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.memberSince')}</span>
          <span className="text-sm text-gray-900 dark:text-white">{formatDate(user.createdAt)}</span>
        </div>
      </div>

      {/* License Section */}
      <div className="space-y-1">
        <div className="flex items-center justify-between px-2 py-1">
          <h2 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
            {t('license.subscriptionInfo')}
          </h2>
          {!isAdmin && !isEditingLicense && (
            <button
              onClick={() => setIsEditingLicense(true)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
          {isEditingLicense && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleSaveLicense}
                disabled={isProcessing}
                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isProcessing ? <Spinner size="sm" /> : <CheckIcon className="h-4 w-4" />}
              </button>
              <button
                onClick={handleCancelLicenseEdit}
                disabled={isProcessing}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {isAdmin ? (
          <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/10">
            <span className="text-sm text-amber-700 dark:text-amber-400">
              {t('license.adminUnlimited')}
            </span>
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded">
              Admin ∞
            </span>
          </div>
        ) : !sub && !isEditingLicense ? (
          /* No license assigned */
          <div className="px-4 py-6 rounded-lg bg-gray-50 dark:bg-slate-800/50 text-center">
            <ExclamationTriangleIcon className="h-8 w-8 mx-auto text-gray-400 dark:text-slate-500 mb-2" />
            <p className="text-sm font-medium text-gray-600 dark:text-slate-400">
              {t('license.noLicense')}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
              {t('license.noLicenseDesc')}
            </p>
            <button
              onClick={() => setIsEditingLicense(true)}
              className="mt-3 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            >
              {t('license.assignLicense')}
            </button>
          </div>
        ) : (
          <>
            {/* Plan Tier */}
            <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
              <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.columns.plan')}</span>
              {isEditingLicense ? (
                <select
                  value={editPlanTier}
                  onChange={(e) => setEditPlanTier(e.target.value as 'starter' | 'pro')}
                  className="px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                >
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                </select>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={clsx(
                    'px-2 py-0.5 text-xs font-medium rounded',
                    sub?.planTier === 'pro'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                  )}>
                    {sub?.planTier === 'pro' ? 'Pro' : 'Starter'}
                  </span>
                  {isManual && (
                    <span className="text-[10px] text-purple-600 dark:text-purple-400">(Manual)</span>
                  )}
                </div>
              )}
            </div>

            {/* Status - only show if subscription exists */}
            {sub && (
              <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="text-sm text-gray-500 dark:text-slate-400">{t('users.columns.status')}</span>
                <div className="flex items-center gap-1.5">
                  {sub.status === 'active' && !expired ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500" />
                  )}
                  <span className={clsx(
                    'text-sm',
                    sub.status === 'active' && !expired
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  )}>
                    {expired ? 'Expired' : sub.status}
                  </span>
                </div>
              </div>
            )}

            {/* Source - only show if subscription exists */}
            {sub && (
              <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="text-sm text-gray-500 dark:text-slate-400">{t('license.source')}</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {sub.source === 'manual' ? t('license.manual') : t('license.googlePlay')}
                </span>
              </div>
            )}

            {/* Expires At */}
            <div className="px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  {isEditingLicense ? t('license.duration') : t('license.expiresAt')}
                </span>
                {!isEditingLicense && (
                  <span className={clsx(
                    'text-sm',
                    expired && 'text-red-600 dark:text-red-400',
                    daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0 && 'text-yellow-600 dark:text-yellow-400',
                    (!expired && (daysUntilExpiry === null || daysUntilExpiry > 7)) && 'text-gray-900 dark:text-white'
                  )}>
                    {expiresAt ? (
                      <>
                        {formatDate(expiresAt)}
                        {daysUntilExpiry !== null && daysUntilExpiry > 0 && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({daysUntilExpiry} {t('license.daysRemaining')})
                          </span>
                        )}
                      </>
                    ) : (
                      sub?.source === 'google_play' ? t('license.googlePlay') : '-'
                    )}
                  </span>
                )}
              </div>
              {isEditingLicense && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { months: 1, label: t('license.duration1Month') },
                    { months: 3, label: t('license.duration3Months') },
                    { months: 6, label: t('license.duration6Months') },
                    { months: 12, label: t('license.duration1Year') },
                  ].map(({ months, label }) => {
                    const targetDate = new Date();
                    targetDate.setMonth(targetDate.getMonth() + months);
                    const dateValue = targetDate.toISOString().split('T')[0];
                    const isSelected = editExpiresAt === dateValue;

                    return (
                      <button
                        key={months}
                        type="button"
                        onClick={() => setEditExpiresAt(dateValue)}
                        className={clsx(
                          'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
                          isSelected
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500'
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              {isEditingLicense && editExpiresAt && (
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  {t('license.expiresAt')}: {formatDate(editExpiresAt)}
                </p>
              )}
            </div>

            {/* Reseller Name - always editable for manual licenses */}
            {(isManual || isEditingLicense) && (
              <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="text-sm text-gray-500 dark:text-slate-400">{t('license.resellerName')}</span>
                {isEditingLicense ? (
                  <input
                    type="text"
                    value={editResellerName}
                    onChange={(e) => setEditResellerName(e.target.value)}
                    placeholder={t('license.resellerNamePlaceholder')}
                    className="px-2 py-1 text-sm text-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder:text-gray-400"
                  />
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {sub?.resellerName || '-'}
                  </span>
                )}
              </div>
            )}

            {/* Notes - always editable */}
            {isEditingLicense && (
              <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                <span className="text-sm text-gray-500 dark:text-slate-400">{t('license.notes')}</span>
                <input
                  type="text"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder={t('license.notesPlaceholder')}
                  className="px-2 py-1 text-sm text-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder:text-gray-400"
                />
              </div>
            )}

            {/* Google Play Note */}
            {!isManual && sub?.source === 'google_play' && !isEditingLicense && (
              <div className="px-4 py-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {t('license.googlePlayNote')}
                </p>
              </div>
            )}

            {/* Cancel License Action */}
            {isManual && sub?.status === 'active' && !isEditingLicense && (
              <div className="px-4 pt-2">
                <button
                  onClick={handleCancelLicense}
                  disabled={isProcessing}
                  className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                >
                  {t('license.cancel')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Danger Zone - Delete User */}
      <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/10">
          <div className="flex items-center gap-3">
            <TrashIcon className="h-5 w-5 text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-400">
                {t('users.deleteUser')}
              </p>
            </div>
          </div>
          <button
            onClick={handleDeleteUser}
            disabled={isProcessing}
            className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            {t('users.deleteUser')}
          </button>
        </div>
      </div>
    </div>
  );
}
